import { Assert, Branded } from '@cotera/utilities';

const SEPARATOR = '|';
const BASE = 10;
const LEXICAL_WIDTH = 5;
export const MAX_NUMBER = BASE ** LEXICAL_WIDTH;

export type StackframeKey = Branded<string, 'StackframeKey'>;

/**
 * Makes a root frame key
 */
const makeRoot = (retryNumber: number = 0): StackframeKey =>
  pad(retryNumber) as StackframeKey;

const getIndex = (frameKey: StackframeKey): number => {
  if (frameKey === '') {
    throw new Error('Cannot have empty frame key.');
  }

  const parts = frameKey.split(SEPARATOR);

  if (parts.length <= 1) {
    return 0;
  }

  return parseInt(parts[parts.length - 1]!, BASE);
};

const increment = (frameKey: StackframeKey): StackframeKey => {
  return makeRoot(getRetryNumber(frameKey) + 1);
};

/**
 * Given the "index" of a stack frame and it's parent frameKey, generate
 * the child stack frame's frameKey.
 */
const makeChild = (
  previousFrameKey: StackframeKey,
  ffiResultsIdx: number
): StackframeKey => {
  return `${previousFrameKey}${SEPARATOR}${pad(
    ffiResultsIdx
  )}` as StackframeKey;
};

/**
 * Returns the retry number of a frameKey.
 */
const getRetryNumber = (frameKey: StackframeKey): number => {
  if (frameKey === '') {
    throw new Error('Cannot have empty frame key.');
  }

  const parts = frameKey.split(SEPARATOR).map((x) => parseInt(x, BASE));

  return Assert.assertExists(parts[0]);
};

/**
 * Return the number array representation of a frameKey.
 */
const asNumArray = (frameKey: StackframeKey): number[] => {
  if (frameKey === '') {
    throw new Error('Cannot have empty frame key.');
  }
  return frameKey.split(SEPARATOR).map((x) => parseInt(x, BASE));
};

const parent = (curr: StackframeKey | number[]): StackframeKey | null => {
  if (isRoot(curr)) {
    return null;
  }

  const numArray = typeof curr === 'string' ? asNumArray(curr) : curr;
  numArray.pop();
  return fromNumArray(numArray);
};

/**
 * Given the "number array representation" of a frameKey, return the
 * lexically ordered string representation.
 */
const fromNumArray = (nums: number[]): StackframeKey => {
  if (nums.length === 0) {
    throw new Error('Cannot have empty frame key');
  }
  return nums.map((num) => pad(num)).join(SEPARATOR) as StackframeKey;
};

/**
 * Given the frameKey of a stack-frame, returns the stack-frame's call-depth.
 */
const depth = (frameKey: string): number =>
  frameKey.split(SEPARATOR).length - 1;

const isRoot = (key: string | number[]): boolean =>
  typeof key === 'string' ? depth(key) === 0 : key.length === 1;

/**
 * A "Frame Key" is a component of a stack-frame's idempotency key that
 * is unique among the the retry-able stack-frames of a single ATC Run Key.
 *
 * The string representation (frameKey) is lexically ordered and is implemented
 * as an array of fixed-width 0-padded numbers joined by a separator. The numbers
 * denote the "index" of each stack-frame in the call-stack leading up to (and including)
 * the frame key's stack-frame.
 *
 * The "index" of a stack-frame is its chronological execution index (including retries)
 * in the list of immediate sibling stack-frames who share the stack frame's parent. For non-root
 * nodes, this index is called the "FFI index."
 *
 * The root stack-frame has no siblings; it's index refers exactly to it's 0-based attempt/retry
 * number.
 */
export const FrameKeyHelpers = {
  parent,
  isRoot,
  makeChild,
  makeRoot,
  getRetryNumber,
  asNumArray,
  fromNumArray,
  getIndex,
  increment,
};

export const validNum = (x: number): boolean =>
  Number.isInteger(x) && x >= 0 && x <= MAX_NUMBER;

export const pad = (x: number): string => {
  Assert.assert(
    validNum(x),
    `The number "${x}" is either non integer or out of range [0, ${MAX_NUMBER}]`
  );

  return x.toString(BASE).padStart(LEXICAL_WIDTH, '0');
};
